import Button from "../Button";
import React, {useContext, useEffect, useState} from "react";
import Icon from "../Icon";
import {Spinner} from "react-bootstrap";
import * as vars from "../../shared/variables";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useUserLoggedIn} from "../../hooks/useUserLoggedIn";
import {useCountryLang} from "../../hooks/useCountryLang";
import {getLocalStorageItem, setLocalStorageItem} from "../../shared/localStorageUtil";
import {ILoggedOutShoppingList} from "../../interfaces/ILoggedOutShoppingList";
import {LoggedOutCartContext} from "../../contexts/LoggedOutCartContext";
import {AddItemType} from "../../types/AddItemType";
import {request} from "../../shared/httpInterceptor";
import {toast} from "react-toastify";
import {useMessages} from "../../hooks/useMessages";
import {useAtom} from "jotai";
import {nextSessionAtom} from "../../store/sessionAtom";
import {addToCartGEvent, GTagAddToCart,} from "../GTagEvent";
import {IProductOption} from "../../interfaces/iProductOption";
import Modal from "react-bootstrap/Modal";
import {ProductSetupOption, removeLoggedOutSetupOption} from "../ProductSetupOption";
import {useShoppingCart} from "../../hooks/useShoppingCart";
import {ICartItem} from "../../interfaces/ICartItem";


const fetchSetupOptions = async (productId: number, countryLang: string | null) => {
  const res = await request({
    url: `/${countryLang}/productSetupOptions?productIds=${productId}`,
    method: 'GET'
  });
  return res.data;
};

type AddToCartPropsType = {
  productId: number,
  quantity?: number,
  icon?: boolean,
  variant?: "primary" | "secondary" | "transparent",
  width?: string,
  className?: string,
  layout: "default" | "remove",
  buttonMessage?: string,
  iconSize?: "xSmall" | "small" | "medium" | "large",
  //Google Analytics
  gaCId: string , //Google tag conversion id
  currency: string,
  price: number,
  name: string //Product name
}

const AddToCart = (props: AddToCartPropsType) => {
  const countryLang = useCountryLang();
  const {productId, quantity, icon, variant, width,
    className, layout, name, currency, price, gaCId} = props;
  const [isLoading, setIsLoading] = useState(false)
  const [iconColor, setIconColor] = useState("")
  const queryClient = useQueryClient()
  const isUserLoggedIn = useUserLoggedIn();
  const {setLoggedOutCartCtx} = useContext(LoggedOutCartContext)
  const [sessionAtom] = useAtom(nextSessionAtom)
  const {session, status} = sessionAtom;
  const messagesQuery = useMessages()
  const [setupOptionsContent, setSetupOptionContent] = useState<IProductOption[]>([])
  const shoppingCartQuery = useShoppingCart()
  const {loggedOutCartCtx} = useContext(LoggedOutCartContext)
  const [loggedOutCartItems, setLoggedOutCartItems] = useState<ICartItem[]>(
    (loggedOutCartCtx as unknown as ICartItem[]) || []
  )

  const addToCartConfirmation = () => {
    toast(messagesQuery.data.messages["product-added-to-cart"], {
      type: "success"
    })
  }

  const addMutation = useMutation(async (item: AddItemType) => {
      setSetupOptionContent([])
      return request({url: `/${countryLang}/addToCart`, data: item, method: 'post', session: session})
    },
    {
      onMutate: () => {
        setIsLoading(true)
      },
      onSuccess: async (res) => {
        setIsLoading(false)
        addToCartConfirmation()

        if (res.data && res.data.length > 0) {
          setSetupOptionContent(res.data)
        }
        await queryClient.invalidateQueries(["shopping-cart", countryLang]);
        await queryClient.invalidateQueries(["checkout-shopping-cart", countryLang]);
      }
  })

  const deleteMutation = useMutation(async (productId: number) => {
      return request({
        url: `/${countryLang}/deleteCartPosition?productId=${productId}`,
        method: 'delete',
        session: session
      })
    },
    {
      onMutate: () => {
        setIsLoading(true)
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(["shopping-cart", countryLang]);
        await queryClient.invalidateQueries(["checkout-shopping-cart", countryLang]);
        setIsLoading(false)
      }
    }
  )

  useEffect(() => {
    variant == "primary" ? setIconColor("#fff") : setIconColor(vars.primaryColor);
  }, [variant])

  // If the user is not logged in we store the cart selection in localStorage
  // To be merged on login with account cart
  const persistLoggedOutCart = async (productId: number, quantity: number) => {
    const res = await request({url: `/${countryLang}/product/${productId}`, method: 'get'})
    const product = res.data
    const entry = {
      product,
      quantity
    }
    const persistedCart = getLocalStorageItem("loggedOutCart");
    if (persistedCart!=null) {
      const cartProducts = [...JSON.parse(persistedCart)]
      if (!cartProducts.some((item: ILoggedOutShoppingList) => item.product.productID==entry.product.productID)) {
        cartProducts.push(entry)
        setLocalStorageItem("loggedOutCart", JSON.stringify(cartProducts))
        setLoggedOutCartCtx(cartProducts)
      }
    } else {
      setLocalStorageItem("loggedOutCart", JSON.stringify([entry]))
      setLoggedOutCartCtx([entry])
    }
    addToCartConfirmation()
  }

  const handleRemoveItemFromLoggedOutCart = () => {
    const persistedCart = getLocalStorageItem("loggedOutCart");
    if (persistedCart!=null) {
      const entries = JSON.parse(persistedCart);
      const filtered = entries.filter((item: ILoggedOutShoppingList) => item.product.productID!=productId);
      setLocalStorageItem("loggedOutCart", JSON.stringify(filtered))
      setLoggedOutCartCtx(filtered)

      // also check if the prouct has any setupOptions added and clear as well
      removeLoggedOutSetupOption(productId)
    }
  }

  const getPurchaseGTagObject = () : GTagAddToCart => {
    return {
      currency: currency,
      value: price,
      items: [
        {
          item_id: productId.toString(),
          item_name: name,
          price: price,
          quantity: quantity!=null ? quantity : 1
        }
      ]
    }
  }

  const fireAddToCartEvent = () => {
    addToCartGEvent(getPurchaseGTagObject(), gaCId)
  }

  const handleAddToCart = async () => {
    fireAddToCartEvent() //Fire add to cart Google Analytics event

    if (!isUserLoggedIn) {
      await persistLoggedOutCart(productId, 1);
      queryClient.invalidateQueries(["loggedOutCartTotal"])
      // check if there are any setupOptions
      // if so, populate and show the modal
      const setupOptions = await fetchSetupOptions(productId, countryLang)
      if (setupOptions && setupOptions[productId]) {
        setSetupOptionContent(setupOptions[productId])
      }
    }
    else {
      const item = {
        productId: productId,
        quantity: 1
      }
      addMutation.mutate(item)
    }
  }

  const handleRemoveFromCart = () => {
    if (isUserLoggedIn) {
      // if the user is logged in we delete the product from the account cart
      deleteMutation.mutate(productId);
    } else {
      // else we remove from localStorage persistence
      handleRemoveItemFromLoggedOutCart()
      queryClient.invalidateQueries(["loggedOutCartTotal"])
    }

  }

  return (
    <>
      {((icon && layout == "default") && !isLoading) &&
        <Button variant={variant} width={width}
                className={"d-flex justify-content-center align-items-center" + className}
                onClick={handleAddToCart}>
          <Icon color={iconColor} variant={props.iconSize ? props.iconSize : "medium"}>
            shopping-cart-line
          </Icon>
        </Button>
      }

      {((!icon && layout == "default") && !isLoading) &&
        <Button variant={variant} width={width} className={className} onClick={handleAddToCart}>
          {props.buttonMessage}
        </Button>
      }

      {(layout == "remove" && !isLoading) &&
        <Button variant={variant} width={width} className={"d-flex justify-content-center " + className}
                onClick={handleRemoveFromCart}>
          <Icon className={"d-inline-block"} variant="small" color={iconColor}>delete-bin</Icon>
        </Button>
      }

      {isLoading &&
        <Button variant={variant} width={width} className={className} disabled={true}>
          <Spinner animation="border" size="sm"/>
        </Button>
      }

      {setupOptionsContent.length > 0 && messagesQuery.data &&
        <Modal show={setupOptionsContent.length > 0}
               onHide={() => setSetupOptionContent([])}
        >
          <Modal.Body>
            <div className={"modal-body-title"}>
              <span>{messagesQuery.data.messages["product-setup"]}</span>
              <Icon variant={"medium"}
                    onClick={() => setSetupOptionContent([])}
                    className={"close-icon"}>close-line</Icon>

            </div>
            <ProductSetupOption options={setupOptionsContent}
                                productId={productId}
                                quantity={1}
                                closeModal={() => setSetupOptionContent([])}
                                shoppingCart={shoppingCartQuery.data ? shoppingCartQuery.data.products : loggedOutCartItems}
            />
          </Modal.Body>
        </Modal>
      }
    </>
  )

}

export default AddToCart;
